import React, { useState, useEffect, useCallback } from "react";
import { Building, ShieldCheck, DollarSign, Award, Clock, Check, CalendarDays, X, Download, Plus, Trash2, Heart, ChevronRight } from "lucide-react";
import api, { BASE } from "../api";
import { K } from "../theme";
import { Cd, Bg, Bt, Sec, Tbl, Av, Sl, Inp } from "../components";
import { notify } from "../notify";
import { fmtDMY, fmtDDMMYY } from "../dateFormat";

export const CompliancePage = ({ cmpData: initData, cmpFilings: initFilings = [], cmpEmps: initEmps = [], onRefresh, role, can = () => true }) => {
  const allCmpTabs = ["dashboard", "filings", "pf", "ghi", "employees", "gst"];
  const visibleCmpTabs = allCmpTabs.filter(t => can('compliance', t, null, 'view'));
  const [cmpTab, setCmpTab] = useState(visibleCmpTabs[0] || "dashboard");
  const [cmpFilter, setCmpFilter] = useState("all");
  const [company, setCompany] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [CD, setCD] = useState(initData || {});
  const [FL, setFL] = useState(initFilings);
  const [CE, setCE] = useState(initEmps);
  const [genOpen, setGenOpen] = useState(false);
  const [fileModal, setFileModal] = useState(null);
  // GST Register (Phase 2)
  const [gstFilters, setGstFilters] = useState({ from: "", to: "", company: "all" });
  const [gstData, setGstData] = useState({ rows: [], totals: { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0, count: 0 } });
  const [form26Open, setForm26Open] = useState(false);

  const cfg = CD.configs || {};
  const sm = CD.summary || {};
  const upcom = CD.upcoming || [];
  const cts = CD.counts || {};
  // Per-scope flags from the backend — drives which tiles render. A flag
  // missing from the response (older API) is treated as enabled so the
  // page degrades gracefully.
  const enabled = CD.flags || { pf: true, esi: true, pt: true, gratuity: true, tds: true };

  const fmt$ = (v) => v >= 10000000 ? "₹" + (v / 10000000).toFixed(1) + "Cr" : v >= 100000 ? "₹" + (v / 100000).toFixed(1) + "L" : "₹" + (v || 0).toLocaleString('en-IN');
  const stClrF = (s) => s === "Filed" ? K.g : s === "Overdue" ? K.r : K.a;
  const mths = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const fmtPeriod = (p) => { if (!p) return "—"; const [y, m] = p.split("-"); return mths[parseInt(m)] + " " + y; };
  const _now = new Date();
  const _fyStart = (_now.getMonth() + 1) >= 4 ? _now.getFullYear() : _now.getFullYear() - 1;
  const fyLabel = `FY ${_fyStart}-${String(_fyStart + 1).slice(2)}`;
  const companyName = (id) => companies.find(c => String(c.id) === String(id))?.name;

  const load = useCallback((co) => {
    const cq = (co && co !== "all") ? "?company=" + co : "";
    api.get("/api/compliance" + cq).then(d => setCD(d || {})).catch(() => {});
    api.get("/api/compliance/filings" + cq).then(d => setFL(d || [])).catch(() => {});
    api.get("/api/compliance/employees" + cq).then(d => setCE(d || [])).catch(() => {});
  }, []);

  useEffect(() => { api.get("/api/companies?active=true").then(d => setCompanies(Array.isArray(d) ? d : d.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(company); }, [company, load]);

  const loadGstRegister = async (f = gstFilters) => {
    const qs = new URLSearchParams();
    if (f.from) qs.set('from', f.from);
    if (f.to) qs.set('to', f.to);
    if (f.company && f.company !== 'all') qs.set('company', f.company);
    try { const d = await api.get('/api/vendor-bills/gst-register?' + qs.toString()); setGstData(d || { rows: [], totals: {} }); } catch (e) { /* noop */ }
  };
  useEffect(() => { if (cmpTab === 'gst') loadGstRegister(); }, [cmpTab]); // eslint-disable-line

  const downloadFile = async (path, name) => {
    try {
      const r = await fetch(`${BASE}${path}`, { headers: { Authorization: "Bearer " + api.getToken() } });
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
    } catch (e) { notify(e.message || "Download failed"); }
  };
  const exportFilings = () => {
    const qs = []; if (company !== "all") qs.push("company=" + company); if (cmpFilter !== "all") qs.push("type=" + cmpFilter);
    downloadFile("/api/compliance/filings/export" + (qs.length ? "?" + qs.join("&") : ""), "compliance-filings.xlsx");
  };

  const filteredFL = cmpFilter === "all" ? FL : FL.filter(f => f.type === cmpFilter);
  const cmpTabs = visibleCmpTabs;

  const CmpTabBar = () => (
    <div className="flex gap-0 mb-3 border-b border-b0 flex-wrap whitespace-nowrap">
      {cmpTabs.map(t => (
        <button
          key={t}
          onClick={() => setCmpTab(t)}
          className={`px-4 py-[7px] bg-transparent border-0 text-[14px] cursor-pointer ${cmpTab === t ? "border-b-2 border-p text-p font-semibold" : "border-b-2 border-transparent text-t2 font-medium"}`}
        >
          {t === "dashboard" ? "Dashboard"
            : t === "filings" ? "Filing Tracker (" + FL.length + ")"
            : t === "pf" ? "PF Sheet"
            : t === "ghi" ? "GHI"
            : t === "gst" ? "GST Register"
            : "Employee Registry (" + CE.length + ")"}
        </button>
      ))}
    </div>
  );

  // ── Mark Filed modal — captures challan + amounts + remarks ──
  const MarkFiledModal = () => {
    const f = fileModal;
    const [v, setV] = useState({
      amount: f.amount || "", erAmt: f.erAmt || "", eeAmt: f.eeAmt || "", empCount: f.empCount || "",
      challan: f.challan || "", filed_date: new Date().toISOString().slice(0, 10), remarks: "",
    });
    const [saving, setSaving] = useState(false); const [err, setErr] = useState("");
    // TDS auto-suggestion from vendor bills for the period (Phase 2 link).
    const [tdsSugg, setTdsSugg] = useState(null);
    useEffect(() => {
      if (f.type !== 'TDS' || !f.period) return;
      const qs = company && company !== 'all' ? '?company_id=' + company : '';
      api.get('/api/compliance/tds-suggest/' + f.period + qs)
        .then(d => setTdsSugg(d || null))
        .catch(() => setTdsSugg(null));
    }, [f.type, f.period]);
    const save = async () => {
      if (!v.challan.trim()) return setErr("Challan number is required");
      if (!(parseFloat(v.amount) > 0)) return setErr("Enter the amount paid");
      setSaving(true); setErr("");
      try {
        await api.put("/api/compliance/filings/" + f.id, {
          status: "Filed", filed_date: v.filed_date, challan_number: v.challan.trim(),
          amount: parseFloat(v.amount) || 0, employer_amount: parseFloat(v.erAmt) || 0,
          employee_amount: parseFloat(v.eeAmt) || 0, employee_count: parseInt(v.empCount) || 0,
          remarks: v.remarks || null,
        });
        setFileModal(null); load(company); onRefresh?.();
        notify(`Filing recorded — ${f.type} · ${fmtPeriod(f.period)}`, { title: "Saved", variant: "success" });
      } catch (e) { setErr(e.message || "Failed"); notify(e.message || "Failed to record filing", { title: "Error", variant: "error" }); } finally { setSaving(false); }
    };
    return (
      <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={() => setFileModal(null)}>
        <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[684px] max-h-[88vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
          <div className="flex justify-between mb-1"><span className="text-base font-extrabold text-t0">Record Filing — {f.type}</span><button onClick={() => setFileModal(null)} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
          <div className="text-[14px] text-t2 mb-4">{fmtPeriod(f.period)}{f.company ? ` · ${f.company}` : ""}</div>
          {tdsSugg && tdsSugg.total > 0 && (
            <div className="mb-3 p-2 rounded-lg flex items-center justify-between gap-2" style={{ background: K.blb, border: `1px solid ${K.bl}33` }}>
              <div>
                <div className="text-[14px] font-bold text-bl">Suggested: ₹{tdsSugg.total.toLocaleString('en-IN')}</div>
                <div className="text-[12px] text-t2">From {tdsSugg.billCount} vendor bill(s) in {fmtPeriod(f.period)}{tdsSugg.breakdown.length > 0 ? ` · ${tdsSugg.breakdown.map(b => `${b.section}: ₹${b.total.toLocaleString('en-IN')}`).join(' · ')}` : ''}</div>
              </div>
              <Bt v="secondary" s="xs" onClick={() => setV(p => ({ ...p, amount: String(tdsSugg.total) }))}>Use this</Bt>
            </div>
          )}
          <div className="grid grid-cols-2 gap-[10px] mb-3">
            <div><div className="text-[14px] text-t2 mb-[3px]">Amount Paid (₹) <span className="text-r">*</span></div><Inp type="number" value={v.amount} onChange={x => setV({ ...v, amount: x })} /></div>
            <div><div className="text-[14px] text-t2 mb-[3px]">Challan Number <span className="text-r">*</span></div><Inp value={v.challan} onChange={x => setV({ ...v, challan: x })} placeholder="e.g. CIN/CRN" /></div>
            <div><div className="text-[14px] text-t2 mb-[3px]">Employer Portion (₹)</div><Inp type="number" value={v.erAmt} onChange={x => setV({ ...v, erAmt: x })} /></div>
            <div><div className="text-[14px] text-t2 mb-[3px]">Employee Portion (₹)</div><Inp type="number" value={v.eeAmt} onChange={x => setV({ ...v, eeAmt: x })} /></div>
            <div><div className="text-[14px] text-t2 mb-[3px]">No. of Employees</div><Inp type="number" value={v.empCount} onChange={x => setV({ ...v, empCount: x })} /></div>
            <div><div className="text-[14px] text-t2 mb-[3px]">Filed Date</div><Inp type="date" value={v.filed_date} onChange={x => setV({ ...v, filed_date: x })} /></div>
          </div>
          <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Remarks</div><textarea value={v.remarks} onChange={e => setV({ ...v, remarks: e.target.value })} placeholder="Optional notes…" className="w-full min-h-[44px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" /></div>
          {err && <div className="text-[14px] text-r mb-2">{err}</div>}
          <div className="flex gap-[6px]"><Bt v="success" full icon={Check} onClick={save} disabled={saving}>{saving ? "Saving…" : "Mark as Filed"}</Bt><Bt v="secondary" full onClick={() => setFileModal(null)}>Cancel</Bt></div>
        </div>
      </div>
    );
  };

  // ── Form 26Q (quarterly TDS return) Excel export ──
  const Form26QModal = ({ companies: cos, defaultCompany, defaultFy, onClose }) => {
    const [fy, setFy] = useState(defaultFy && /^\d{4}-\d{2}$/.test(defaultFy) ? defaultFy : `${_fyStart}-${String(_fyStart + 1).slice(2)}`);
    const [quarter, setQuarter] = useState('Q1');
    const [cid, setCid] = useState(defaultCompany || '');
    const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
    const run = async () => {
      if (!/^\d{4}-\d{2}$/.test(fy)) return setErr('FY must be like 2025-26');
      setBusy(true); setErr('');
      const qs = new URLSearchParams({ fy, quarter, format: 'xlsx' });
      if (cid && cid !== 'all') qs.set('company', cid);
      try {
        await downloadFile('/api/vendor-bills/form-26q?' + qs.toString(), `form-26q-${fy}-${quarter}.xlsx`);
        onClose();
      } catch (e) { setErr(e.message || 'Download failed'); } finally { setBusy(false); }
    };
    return (
      <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
        <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[600px]" onMouseDown={e => e.stopPropagation()}>
          <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">Form 26Q — Quarterly TDS Return</span><button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
          <div className="grid grid-cols-2 gap-[10px] mb-3">
            <div><div className="text-[14px] text-t2 mb-[3px]">FY</div><Inp value={fy} onChange={setFy} placeholder="2025-26" /></div>
            <div><div className="text-[14px] text-t2 mb-[3px]">Quarter</div><Sl options={[{ v: 'Q1', l: 'Q1 (Apr–Jun)' }, { v: 'Q2', l: 'Q2 (Jul–Sep)' }, { v: 'Q3', l: 'Q3 (Oct–Dec)' }, { v: 'Q4', l: 'Q4 (Jan–Mar)' }]} value={quarter} onChange={setQuarter} /></div>
          </div>
          <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Company</div><Sl options={[{ v: '', l: 'All Companies' }, ...cos.map(c => ({ v: String(c.id), l: c.name }))]} value={cid} onChange={setCid} /></div>
          <div className="text-[14px] text-t2 bg-bg2 border border-b0 rounded-lg px-3 py-2 mb-3">Excel of all TDS-on-non-salary deductions from <b className="text-t0">vendor bills</b> in the quarter — the working dataset your CA needs for filing Form 26Q.</div>
          {err && <div className="text-[14px] text-r mb-2">{err}</div>}
          <div className="flex gap-[6px]"><Bt full icon={Download} onClick={run} disabled={busy}>{busy ? 'Generating…' : 'Download Excel'}</Bt><Bt v="secondary" full onClick={onClose}>Cancel</Bt></div>
        </div>
      </div>
    );
  };

  // ── Generate monthly filing placeholders ──
  const GenModal = () => {
    const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
    const [gc, setGc] = useState(company !== "all" ? company : (companies[0] ? String(companies[0].id) : ""));
    const [saving, setSaving] = useState(false); const [err, setErr] = useState("");
    const gen = async () => {
      if (!gc) return setErr("Select a company");
      if (!period) return setErr("Select a month");
      setSaving(true); setErr("");
      try {
        const r = await api.post("/api/compliance/filings/generate", { period, company_id: parseInt(gc) });
        setGenOpen(false);
        notify(`Generated ${r.created} filing(s)${r.skipped ? `, ${r.skipped} already existed` : ""}`, { title: "Generated", variant: "success" });
        setCompany(String(gc)); load(String(gc));
      } catch (e) { setErr(e.message || "Failed"); notify(e.message || "Failed to generate filings", { title: "Error", variant: "error" }); } finally { setSaving(false); }
    };
    return (
      <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={() => setGenOpen(false)}>
        <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[600px]" onMouseDown={e => e.stopPropagation()}>
          <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">Generate Monthly Filings</span><button onClick={() => setGenOpen(false)} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
          <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Company <span className="text-r">*</span></div><Sl options={companies.map(c => ({ v: String(c.id), l: c.name }))} value={gc} onChange={setGc} /></div>
          <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Month <span className="text-r">*</span></div><Inp type="month" value={period} onChange={setPeriod} /></div>
          <div className="text-[14px] text-t2 bg-bg2 border border-b0 rounded-lg px-3 py-2 mb-3">Creates blank <b className="text-t0">PF, ESI, TDS &amp; PT</b> records with their due dates for this month. Your team fills the amounts &amp; challan when filing.</div>
          {err && <div className="text-[14px] text-r mb-2">{err}</div>}
          <div className="flex gap-[6px]"><Bt full icon={Plus} onClick={gen} disabled={saving}>{saving ? "Generating…" : "Generate"}</Bt><Bt v="secondary" full onClick={() => setGenOpen(false)}>Cancel</Bt></div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-[14px] flex-wrap gap-2">
        <h1 className="m-0 text-2xl font-extrabold text-t0">Compliance</h1>
        <div className="flex items-center gap-[6px] flex-wrap">
          <Sl w={170} options={[{ v: "all", l: "All Companies" }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]} value={company} onChange={setCompany} />
          {upcom.length > 0 && <Bg c={K.a} dot>{upcom.length} upcoming</Bg>}
          <Bg c={K.g} dot>{fyLabel}</Bg>
          {can('compliance', 'filings', null, 'edit') && <Bt v="secondary" s="sm" icon={Plus} onClick={() => setGenOpen(true)}>Generate</Bt>}
          {can('compliance', 'filings', null, 'view') && <Bt v="secondary" s="sm" icon={Download} onClick={exportFilings}>Export</Bt>}
          {can('compliance', 'filings', null, 'edit') && <Bt v="secondary" s="sm" icon={Download} onClick={() => setForm26Open(true)}>Form 26Q</Bt>}
        </div>
      </div>
      <CmpTabBar />

      {cmpTab === "dashboard" && can('compliance', 'dashboard', null, 'view') && <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-[10px] mb-4">
          {[
            { t: "Provident Fund", k: "pf", ic: Building, c: K.bl, d: cfg.pf ? `ER ${cfg.pf.er}%+EE ${cfg.pf.ee}% · Ceil ₹${(cfg.pf.ceil || 15000).toLocaleString('en-IN')}` : "ER 12%+EE 12%", cnt: cts.pf || 0, total: sm.pf?.totalAmount || 0 },
            { t: "ESI", k: "esi", ic: ShieldCheck, c: K.g, d: cfg.esi ? `ER ${cfg.esi.er}%+EE ${cfg.esi.ee}% · Ceil ₹${cfg.esi.wage_ceil || 21000}` : "ER 3.25%+EE 0.75%", cnt: cts.esi || 0, total: sm.esi?.totalAmount || 0 },
            { t: "TDS", k: "tds", ic: DollarSign, c: K.a, d: cfg.tds ? `Due ${cfg.tds.due_day}th · Form 16: ${cfg.tds.form16_due}` : "Due 7th monthly", cnt: sm.tds?.empCount || 0, total: sm.tds?.totalAmount || 0 },
            { t: "Professional Tax", k: "pt", ic: DollarSign, c: K.pu, d: cfg.pt ? Object.entries(cfg.pt.slabs || {}).map(([k, v]) => `${k.toUpperCase()} ₹${v}`).join(" · ") : "State-wise slabs", cnt: sm.pt?.empCount || 0, total: sm.pt?.totalAmount || 0 },
            { t: "Gratuity", k: "gratuity", ic: Award, c: K.tl, d: cfg.gratuity ? `Min ${cfg.gratuity.min_years}yr · Provision ${cfg.gratuity.provision}` : "5yr eligibility", cnt: cts.gratuity || 0, total: 0 },
          ].filter(c => enabled[c.k] !== false).map(c => {
            const s = sm[c.k] || {}; const filed = s.filed || 0; const total = s.total || 0; const pending = s.pending || 0; const overdue = s.overdue || 0;
            return (
              <Cd key={c.t}>
                <div className="flex items-center gap-2 mb-[10px]">
                  <div className="w-9 h-9 rounded-[10px] flex items-center justify-center" style={{ background: `${c.c}10` }}><c.ic size={16} color={c.c} /></div>
                  <div className="flex-1"><div className="font-bold text-[14px] text-t0">{c.t}</div><Bg c={overdue > 0 ? K.r : pending > 0 ? K.a : K.g} sm dot>{overdue > 0 ? "Overdue" : pending > 0 ? "Pending" : "On Track"}</Bg></div>
                </div>
                <div className="text-[14px] text-t2 leading-[1.5] mb-2">{c.d}</div>
                <div className="grid grid-cols-3 gap-[6px]">
                  <div className="text-center px-0 py-1 bg-bg2 rounded-md"><div className="text-[15px] font-extrabold" style={{ color: c.c }}>{c.cnt}</div><div className="text-[12px] text-t3">Employees</div></div>
                  <div className="text-center px-0 py-1 bg-bg2 rounded-md"><div className="text-[15px] font-extrabold text-g">{filed}/{total}</div><div className="text-[12px] text-t3">Filed</div></div>
                  <div className="text-center px-0 py-1 bg-bg2 rounded-md"><div className="text-[15px] font-extrabold text-t0">{fmt$(c.total)}</div><div className="text-[12px] text-t3">YTD</div></div>
                </div>
              </Cd>
            );
          })}
        </div>

        {upcom.length > 0 && <Cd style={{ marginBottom: 12 }}>
          <Sec icon={Clock} title={`Upcoming Deadlines (${upcom.length})`} />
          <Tbl cols={[
            { k: "type", l: "Type", r: u => <Bg c={stClrF(u.status)} sm dot>{u.type}</Bg> },
            { k: "period", l: "Period", r: u => <span className="text-[14px] font-semibold text-t0">{fmtPeriod(u.period)}</span> },
            { k: "status", l: "Status", r: u => u.status === "Overdue" ? <Bg c={K.r} sm>Overdue</Bg> : <Bg c={K.a} sm>{u.status}</Bg> },
            { k: "due", l: "Due", r: u => <span className="text-[14px] text-t2">{fmtDDMMYY(u.due)}</span> },
            { k: "amt", l: "Amount", r: u => <span className="text-[14px] font-bold text-t0">{fmt$(u.amount)}</span> },
            { k: "act", l: "", r: u => <Bt v="success" s="xs" onClick={() => setFileModal(u)}>Record</Bt> },
          ]} data={upcom} />
        </Cd>}

        <Cd>
          <Sec icon={CalendarDays} title="Filing Status — Last 6 Months" />
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[14px]">
              <thead><tr className="bg-bg2">
                <th className="px-2 py-[6px] text-left text-t3 text-[12px]">TYPE</th>
                {Array.from({ length: 6 }, (_, i) => { const d = new Date(); d.setMonth(d.getMonth() - 5 + i); return <th key={i} className="px-2 py-[6px] text-center text-t3 text-[12px]">{mths[d.getMonth() + 1]} {d.getFullYear()}</th>; })}
              </tr></thead>
              <tbody>
                {["PF", "ESI", "TDS", "PT"].map(type => (
                  <tr key={type} className="border-b border-b0">
                    <td className="px-2 py-[5px] font-semibold text-t0">{type}</td>
                    {Array.from({ length: 6 }, (_, i) => {
                      const d = new Date(); d.setMonth(d.getMonth() - 5 + i);
                      const p = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0");
                      const f = FL.find(f => f.type === type && f.period === p);
                      return (
                        <td key={i} className="px-2 py-[5px] text-center">
                          {f ? <Bg c={stClrF(f.status)} sm dot>{f.status === "Filed" ? "✓" : f.status === "Overdue" ? "!" : "⏳"}</Bg> : <span className="text-t3">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Cd>
      </div>}

      {cmpTab === "filings" && can('compliance', 'filings', null, 'view') && <div>
        <div className="flex gap-[6px] mb-3 flex-wrap">
          {["all", "PF", "ESI", "TDS", "PT"].map(f => (
            <button
              key={f}
              onClick={() => setCmpFilter(f)}
              className={`px-3 py-[5px] rounded-md border text-[14px] font-semibold cursor-pointer ${cmpFilter === f ? "border-p bg-pg text-p" : "border-b0 bg-transparent text-t2"}`}
            >
              {f === "all" ? "All" : f}
            </button>
          ))}
        </div>
        <Tbl cols={[
          { k: "type", l: "Type", r: r => <Bg c={r.type === "PF" ? K.bl : r.type === "ESI" ? K.g : r.type === "TDS" ? K.a : r.type === "PT" ? K.pu : K.tl} sm>{r.type}</Bg> },
          { k: "period", l: "Period", r: r => <span className="font-semibold text-[14px]">{fmtPeriod(r.period)}</span> },
          { k: "co", l: "Company", r: r => <span className="text-[14px] text-t1">{r.company || "All"}</span> },
          { k: "due", l: "Due Date", r: r => <span className="text-[14px] text-t1">{fmtDDMMYY(r.due)}</span> },
          { k: "amount", l: "Amount", r: r => <span className="font-bold text-[14px] text-t0">{fmt$(r.amount)}</span> },
          { k: "ee", l: "Employee", r: r => <span className="text-[14px] text-pu">{fmt$(r.eeAmt)}</span> },
          { k: "emp", l: "Emp", r: r => <span className="text-[14px]">{r.empCount}</span> },
          { k: "st", l: "Status", r: r => <Bg c={stClrF(r.status)} sm dot>{r.status}</Bg> },
          { k: "challan", l: "Challan", r: r => <span className="text-[12px] text-t2">{r.challan || "—"}</span> },
          { k: "act", l: "", r: r => r.status !== "Filed" ? <Bt v="success" s="xs" icon={Check} onClick={() => setFileModal(r)}>Record</Bt> : <span className="text-[12px] text-t3">{r.filed || "Filed"}</span> },
        ]} data={filteredFL} />
        {filteredFL.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">No filings — use “Generate” to create this month’s records</div>}
      </div>}

      {cmpTab === "pf" && can('compliance', 'pf', null, 'view') && <PFSheet companies={companies} enabled={enabled} />}
      {cmpTab === "ghi" && can('compliance', 'ghi', null, 'view') && <GHISheet companies={companies} />}

      {cmpTab === "employees" && can('compliance', 'employees', null, 'view') && <div>
        <Tbl cols={[
          { k: "nm", l: "Employee", r: r => <div className="flex items-center gap-[5px]"><Av nm={r.nm} sz={22} /><div><div className="text-[15px] font-bold text-t0">{r.nm}</div><div className="text-[12px] text-t3">{r.eid} · {r.dept}</div></div></div> },
          { k: "pf", l: "PF", r: r => r.pf ? <Bg c={K.g} sm dot>{r.pfNo || "Enrolled"}</Bg> : <span className="text-[12px] text-t3">N/A</span> },
          { k: "uan", l: "UAN", r: r => <span className="text-[12px] text-t1">{r.uan || "—"}</span> },
          { k: "esi", l: "ESI", r: r => r.esi ? <Bg c={K.bl} sm dot>{r.esiNo || "Enrolled"}</Bg> : <span className="text-[12px] text-t3">N/A</span> },
          { k: "pan", l: "PAN", r: r => <span className="text-[12px] text-t1 font-mono">{r.pan || "—"}</span> },
          { k: "regime", l: "Tax", r: r => <Bg c={r.regime === "New" ? K.bl : K.a} sm>{r.regime || "New"}</Bg> },
          { k: "basic", l: "Basic", r: r => <span className="text-[14px] font-semibold text-t0">{r.basic ? fmt$(r.basic) : "—"}</span> },
          { k: "doj", l: "DOJ", r: r => <span className="text-[12px] text-t2">{r.doj || "—"}</span> },
        ]} data={CE} />
        {CE.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">No employee compliance data</div>}
      </div>}

      {cmpTab === "gst" && can('compliance', 'gst', null, 'view') && <div>
        <div className="text-[14px] text-t2 mb-3 max-w-[680px]">Input GST credit from vendor bills — CGST / SGST / IGST paid to vendors that you can claim at GST return time. Filter by date and company.</div>
        <div className="flex flex-wrap gap-2 mb-3 items-end">
          <div><div className="text-[12px] text-t3 mb-[2px]">From</div><Inp type="date" value={gstFilters.from} onChange={v => setGstFilters(p => ({ ...p, from: v }))} style={{ width: 140 }} /></div>
          <div><div className="text-[12px] text-t3 mb-[2px]">To</div><Inp type="date" value={gstFilters.to} onChange={v => setGstFilters(p => ({ ...p, to: v }))} style={{ width: 140 }} /></div>
          <div><div className="text-[12px] text-t3 mb-[2px]">Company</div><Sl w={170} options={[{ v: "all", l: "All Companies" }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]} value={gstFilters.company} onChange={v => setGstFilters(p => ({ ...p, company: v }))} /></div>
          <Bt s="sm" onClick={() => loadGstRegister()}>Apply</Bt>
          <Bt v="ghost" s="sm" onClick={() => { const r = { from: "", to: "", company: "all" }; setGstFilters(r); loadGstRegister(r); }}>Reset</Bt>
          <Bt v="secondary" s="sm" icon={Download} onClick={() => {
            const qs = new URLSearchParams({ format: 'xlsx' });
            if (gstFilters.from) qs.set('from', gstFilters.from);
            if (gstFilters.to) qs.set('to', gstFilters.to);
            if (gstFilters.company !== 'all') qs.set('company', gstFilters.company);
            (async () => {
              try {
                const r = await fetch(`${BASE}/api/vendor-bills/gst-register?${qs.toString()}`, { headers: { Authorization: 'Bearer ' + api.getToken() } });
                if (!r.ok) throw new Error('Download failed');
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = 'gst-register.xlsx'; a.click(); URL.revokeObjectURL(url);
              } catch (e) { notify(e.message || 'Download failed'); }
            })();
          }}>Excel</Bt>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-[8px] mb-3">
          {[
            { l: 'Bills', v: gstData.totals?.count || 0, c: K.p },
            { l: 'Taxable', v: '₹' + (gstData.totals?.taxable || 0).toLocaleString('en-IN'), c: K.t0 },
            { l: 'CGST', v: '₹' + (gstData.totals?.cgst || 0).toLocaleString('en-IN'), c: K.bl },
            { l: 'SGST', v: '₹' + (gstData.totals?.sgst || 0).toLocaleString('en-IN'), c: K.g },
            { l: 'IGST', v: '₹' + (gstData.totals?.igst || 0).toLocaleString('en-IN'), c: K.pu },
          ].map((k, i) => <Cd key={i} style={{ padding: 10 }}>
            <div className="text-[12px] text-t3 uppercase tracking-wide">{k.l}</div>
            <div className="text-[15px] font-extrabold mt-[2px]" style={{ color: k.c }}>{k.v}</div>
          </Cd>)}
        </div>

        <Tbl cols={[
          { k: "d", l: "Bill Date", r: r => <span className="text-[14px] text-t1">{fmtDDMMYY(r.billDate) || "—"}</span> },
          { k: "v", l: "Vendor", r: r => <div><div className="text-[14px] font-semibold text-t0">{r.vendor || "—"}</div><div className="text-[12px] text-t3 font-mono">{r.gstin || ''}</div></div> },
          { k: "co", l: "Company", r: r => <span className="text-[14px] text-t1">{r.company || "—"}</span> },
          { k: "cat", l: "Category", r: r => <span className="text-[14px] text-t1">{r.category || "—"}</span> },
          { k: "bn", l: "Bill No", r: r => <span className="text-[12px] text-t2 font-mono">{r.billNo || "—"}</span> },
          { k: "tx", l: "Taxable", r: r => <span className="text-[14px] text-t0">{'₹' + (r.taxable || 0).toLocaleString('en-IN')}</span> },
          { k: "c", l: "CGST", r: r => <span className="text-[14px] text-bl">{r.cgst ? '₹' + r.cgst.toLocaleString('en-IN') : "—"}</span> },
          { k: "s", l: "SGST", r: r => <span className="text-[14px] text-g">{r.sgst ? '₹' + r.sgst.toLocaleString('en-IN') : "—"}</span> },
          { k: "i", l: "IGST", r: r => <span className="text-[14px] text-pu">{r.igst ? '₹' + r.igst.toLocaleString('en-IN') : "—"}</span> },
          { k: "t", l: "Total", r: r => <span className="text-[14px] font-bold text-t0">{'₹' + (r.total || 0).toLocaleString('en-IN')}</span> },
          { k: "st", l: "Status", r: r => <Bg c={r.status === 'Paid' ? K.g : r.status === 'Recorded' ? K.a : K.t3} sm dot>{r.status}</Bg> },
        ]} data={gstData.rows} />
        {(!gstData.rows || gstData.rows.length === 0) && <div className="text-center p-[30px] text-t3 text-[14px]">No vendor bills with GST for the selected filters</div>}
      </div>}

      {fileModal && <MarkFiledModal />}
      {genOpen && <GenModal />}
      {form26Open && <Form26QModal companies={companies} defaultCompany={company !== 'all' ? company : ''} defaultFy={fyLabel.replace('FY ', '')} onClose={() => setForm26Open(false)} />}
    </div>
  );
};

// ═══════════════════════════════════════
// PF Sheet — monthly PF working sheet per company, matching the ECR-
// upload format HR keeps. Only lists companies where PF is currently
// enabled (via Settings → Payroll → Statutory Compliance).
// ═══════════════════════════════════════
const PFSheet = ({ companies }) => {
  // Default the period to the most recent completed month (previous month).
  const _lastMonth = () => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
  };
  const [period, setPeriod] = useState(_lastMonth());
  const [companyId, setCompanyId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pfCompanies, setPfCompanies] = useState([]);

  // Discover which companies have PF enabled by hitting the compliance
  // endpoint per company. Cheap — one call per company — but we cache
  // the result so switching periods doesn't re-check.
  useEffect(() => {
    (async () => {
      const results = await Promise.all(
        companies.map(c => api.get('/api/compliance?company=' + c.id)
          .then(d => ({ id: c.id, name: c.name, on: d?.flags?.pf === true }))
          .catch(() => ({ id: c.id, name: c.name, on: false })))
      );
      const enabled = results.filter(r => r.on);
      setPfCompanies(enabled);
      if (enabled.length && !companyId) setCompanyId(String(enabled[0].id));
    })();
  }, [companies]); // eslint-disable-line

  const load = useCallback(() => {
    if (!companyId || !period) return;
    setLoading(true);
    api.get(`/api/compliance/pf-sheet?company_id=${companyId}&period=${period}`)
       .then(d => setData(d))
       .catch(() => setData(null))
       .finally(() => setLoading(false));
  }, [companyId, period]);
  useEffect(() => { load(); }, [load]);

  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
  const download = () => {
    if (!companyId || !period) return;
    const url = `${BASE}/api/compliance/pf-sheet/export?company_id=${companyId}&period=${period}&token=${localStorage.getItem('hrms_token')}`;
    window.open(url, '_blank');
  };

  if (pfCompanies.length === 0) {
    return <Cd><div className="text-center p-8 text-t3 text-[14px]">
      No company has PF enabled. Turn on PF in <b className="text-t0">Settings → Payroll → Statutory Compliance</b> to start generating monthly PF sheets.
    </div></Cd>;
  }

  return <div>
    <Cd style={{ marginBottom: 12 }}>
      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-[220px]">
          <div className="text-[14px] text-t3 uppercase mb-[3px]">Company</div>
          <Sl options={pfCompanies.map(c => ({ v: String(c.id), l: c.name }))}
              value={companyId} onChange={setCompanyId} />
        </div>
        <div className="min-w-[160px]">
          <div className="text-[14px] text-t3 uppercase mb-[3px]">Period (YYYY-MM)</div>
          <Inp type="text" value={period} onChange={setPeriod} placeholder="2026-05" />
        </div>
        <div className="flex-1"></div>
        <Bt icon={Download} onClick={download} disabled={!data?.rows?.length}>Export Excel</Bt>
      </div>
    </Cd>

    {loading && <Cd><div className="text-center p-6 text-t3 text-[14px]">Loading…</div></Cd>}
    {!loading && data && data.rows.length === 0 && (
      <Cd><div className="text-center p-8 text-t3 text-[14px]">
        No PF-applicable employees at this company for {period}.
      </div></Cd>
    )}
    {!loading && data && data.rows.length > 0 && (
      <Cd>
        <div className="flex justify-between items-center mb-2 text-[14px] text-t3">
          <div>PF ceiling ₹{data.pfCeil.toLocaleString('en-IN')} · Rate {data.pfRate}% (EE) + {data.pfRate}% (ER)</div>
          <div><b className="text-t0">{data.totals.count}</b> employees · Total PF <b className="text-t0">{fmt(data.totals.pf)}</b></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[14px]">
            <thead><tr className="bg-bg2 text-t3 text-[12px] uppercase">
              <th className="px-2 py-[6px] text-left">#</th>
              <th className="px-2 py-[6px] text-left">Branch</th>
              <th className="px-2 py-[6px] text-left">Name</th>
              <th className="px-2 py-[6px] text-left">Mobile</th>
              <th className="px-2 py-[6px] text-left">Email</th>
              <th className="px-2 py-[6px] text-left">DOB</th>
              <th className="px-2 py-[6px] text-left">DOJ</th>
              <th className="px-2 py-[6px] text-left">Gender</th>
              <th className="px-2 py-[6px] text-left">PAN</th>
              <th className="px-2 py-[6px] text-left">UAN</th>
              <th className="px-2 py-[6px] text-left">Marital</th>
              <th className="px-2 py-[6px] text-right">Salary</th>
              <th className="px-2 py-[6px] text-right">Basic</th>
              <th className="px-2 py-[6px] text-right">PF Basic</th>
              <th className="px-2 py-[6px] text-right">PF</th>
            </tr></thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.employeeId} className="border-b border-b0 hover:bg-sh">
                  <td className="px-2 py-[5px] text-t2">{r.sno}</td>
                  <td className="px-2 py-[5px] text-t1">{r.branch || '—'}</td>
                  <td className="px-2 py-[5px] text-t0 font-semibold">{r.name}</td>
                  <td className="px-2 py-[5px] text-t2 font-mono text-[12px]">{r.mobile || '—'}</td>
                  <td className="px-2 py-[5px] text-t2 font-mono text-[12px]">{r.email || '—'}</td>
                  <td className="px-2 py-[5px] text-t2">{r.dob || '—'}</td>
                  <td className="px-2 py-[5px] text-t2">{r.doj || '—'}</td>
                  <td className="px-2 py-[5px] text-t2">{r.gender || '—'}</td>
                  <td className="px-2 py-[5px] text-t2 font-mono text-[12px]">{r.pan || '—'}</td>
                  <td className="px-2 py-[5px] text-t2 font-mono text-[12px]">{r.uan || '—'}</td>
                  <td className="px-2 py-[5px] text-t2">{r.marital || '—'}</td>
                  <td className="px-2 py-[5px] text-right text-t1 font-mono">{fmt(r.salary)}</td>
                  <td className="px-2 py-[5px] text-right text-t1 font-mono">{fmt(r.basic)}</td>
                  <td className="px-2 py-[5px] text-right text-t1 font-mono">{fmt(r.pfBasic)}</td>
                  <td className="px-2 py-[5px] text-right text-t0 font-mono font-bold">{fmt(r.pf)}</td>
                </tr>
              ))}
              <tr className="bg-bg2 font-bold text-t0">
                <td colSpan={11} className="px-2 py-[6px] text-right">Total</td>
                <td className="px-2 py-[6px] text-right font-mono">{fmt(data.totals.salary)}</td>
                <td className="px-2 py-[6px] text-right font-mono">{fmt(data.totals.basic)}</td>
                <td className="px-2 py-[6px] text-right font-mono">{fmt(data.totals.pfBasic)}</td>
                <td className="px-2 py-[6px] text-right font-mono">{fmt(data.totals.pf)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="text-[12px] text-t3 mt-2">
          Father Name and Aadhaar are not stored today — those columns are blank in the export. Add them to the exported .xlsx before you upload.
        </div>
      </Cd>
    )}
  </div>;
};

// ═══════════════════════════════════════
// GHI — Group Health Insurance sheet.
// Report roster for the insurer + per-employee family drill-down + the
// "Added to bank portal" toggle. Premium is read from the salary
// structure (not editable here). Sum insured + bank enrollment are the
// two HR-managed fields.
// ═══════════════════════════════════════
const GHISheet = ({ companies }) => {
  const _lastMonth = () => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
  };
  const [period, setPeriod] = useState(_lastMonth());
  const [companyId, setCompanyId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillId, setDrillId] = useState(null); // employee_id being drilled into
  const [togglingId, setTogglingId] = useState(null); // zlCode currently being toggled

  useEffect(() => { if (!companyId && companies.length) setCompanyId(String(companies[0].id)); }, [companies]); // eslint-disable-line

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    api.get(`/api/compliance/ghi/report?company_id=${companyId}&period=${period}`)
       .then(d => setData(d)).catch(() => setData(null))
       .finally(() => setLoading(false));
  }, [companyId, period]);
  useEffect(() => { load(); }, [load]);

  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

  const toggleBankEnrolled = async (row) => {
    setTogglingId(row.zlCode);
    try {
      await api.put(`/api/employees/${row.zlCode}/ghi-enrolled-at-bank`, { enrolled: !row.bankEnrolled });
      load();
      notify(row.bankEnrolled ? 'Marked as not yet on bank portal' : 'Marked as enrolled on bank portal', { title: 'Saved', variant: 'success' });
    } catch (e) { notify(e.message || 'Failed to update bank portal status', { title: 'Error', variant: 'error' }); }
    finally { setTogglingId(null); }
  };

  return <div>
    <Cd style={{ marginBottom: 12 }}>
      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-[220px]">
          <div className="text-[14px] text-t3 uppercase mb-[3px]">Company</div>
          <Sl options={companies.map(c => ({ v: String(c.id), l: c.name }))}
              value={companyId} onChange={setCompanyId} />
        </div>
        <div className="min-w-[200px]">
          <div className="text-[14px] text-t3 uppercase mb-[3px]">Policy Month</div>
          <Sl value={period} onChange={setPeriod} options={(() => {
            // Last 24 months, most recent first. Cheap enough to build inline;
            // no need for a memo since Compliance renders infrequently.
            const opts = [];
            const now = new Date();
            for (let i = 0; i < 24; i++) {
              const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
              const v = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
              const l = d.toLocaleString('default', { month: 'long' }) + ' ' + d.getFullYear();
              opts.push({ v, l });
            }
            return opts;
          })()} />
        </div>
        <div className="flex-1"></div>
      </div>
    </Cd>

    {loading && <Cd><div className="text-center p-6 text-t3 text-[14px]">Loading…</div></Cd>}

    {!loading && data && data.rows.length === 0 && (
      <Cd><div className="text-center p-8 text-t3 text-[14px]">
        No GHI-enrolled employees at this company yet. Add a GHI premium in an employee's Salary Structure to include them here.
      </div></Cd>
    )}

    {!loading && data && data.rows.length > 0 && (
      <Cd>
        <div className="flex justify-between items-center mb-2 text-[14px] text-t3 flex-wrap gap-2">
          <div>{data.company?.name} · {data.period}</div>
          <div>
            <b className="text-t0">{data.totals.count}</b> employees ({data.totals.activeCount} active) ·
            <b className="text-t0"> {data.totals.familyCovered}</b> dependents ·
            Total premium <b className="text-t0">{fmt(data.totals.premium)}/mo</b>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[14px]">
            <thead><tr className="bg-bg2 text-t3 text-[12px] uppercase">
              <th className="px-2 py-[6px] text-left">#</th>
              <th className="px-2 py-[6px] text-left">ZL Code</th>
              <th className="px-2 py-[6px] text-left">Center</th>
              <th className="px-2 py-[6px] text-left">Name</th>
              <th className="px-2 py-[6px] text-left">DOJ</th>
              <th className="px-2 py-[6px] text-left" title="As per Aadhaar Card">DOB</th>
              <th className="px-2 py-[6px] text-left">Status</th>
              <th className="px-2 py-[6px] text-right">Sum Insured</th>
              <th className="px-2 py-[6px] text-right">Premium / mo</th>
              <th className="px-2 py-[6px] text-center">Family</th>
              <th className="px-2 py-[6px] text-center" title="HR ticks after adding on the insurer's portal">On Bank Portal</th>
              <th className="px-2 py-[6px]"></th>
            </tr></thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.zlCode} className="border-b border-b0 hover:bg-sh">
                  <td className="px-2 py-[5px] text-t2">{r.sno}</td>
                  <td className="px-2 py-[5px] font-mono text-[12px] text-t1">{r.zlCode}</td>
                  <td className="px-2 py-[5px] text-t1">{r.center || '—'}</td>
                  <td className="px-2 py-[5px] text-t0 font-semibold">{r.name}</td>
                  <td className="px-2 py-[5px] text-t2">{r.doj || '—'}</td>
                  <td className="px-2 py-[5px] text-t2">{r.dob || '—'}</td>
                  <td className="px-2 py-[5px]"><Bg c={r.status === 'Active' ? K.g : K.t3} sm dot>{r.status}</Bg></td>
                  <td className="px-2 py-[5px] text-right text-t1 font-mono">{fmt(r.sumInsured)}</td>
                  <td className="px-2 py-[5px] text-right text-t1 font-mono">{fmt(r.premium)}</td>
                  <td className="px-2 py-[5px] text-center text-t1">{r.familyCount}</td>
                  <td className="px-2 py-[5px] text-center">
                    <button onClick={() => toggleBankEnrolled(r)} disabled={togglingId === r.zlCode}
                      title={r.bankEnrolled ? `Enrolled ${r.bankEnrolledAt ? fmtDMY(r.bankEnrolledAt) : ''} — click to unmark` : 'Not yet on portal — click to mark enrolled'}
                      className={`text-[12px] font-semibold px-2 py-[3px] rounded border cursor-pointer disabled:opacity-60 ${r.bankEnrolled ? 'bg-gb text-g border-gd' : 'bg-ab text-a border-ad'}`}>
                      {togglingId === r.zlCode ? '…' : (r.bankEnrolled ? 'Enrolled' : 'Pending')}
                    </button>
                  </td>
                  <td className="px-2 py-[5px] text-right">
                    <button onClick={() => setDrillId(r.zlCode)} className="bg-transparent border-0 text-p cursor-pointer flex items-center gap-1 text-[14px] ml-auto">
                      Family <ChevronRight size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Cd>
    )}

    {drillId && <GHIFamilyDrawer employeeId={drillId} onClose={() => setDrillId(null)} onChanged={load} />}
  </div>;
};

// Family drill-down drawer. Reads the same /:id/ghi-summary the employee
// sees, plus lets HR add / edit / remove family members.
const GHIFamilyDrawer = ({ employeeId, onClose, onChanged }) => {
  const [sum, setSum] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', relationship: 'Spouse', dob: '', gender: '' });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => {
    api.get(`/api/employees/${employeeId}/ghi-summary`)
       .then(d => setSum(d))
       .catch(() => setSum(null));
  };
  useEffect(load, [employeeId]);

  const save = async () => {
    if (!form.name.trim()) return notify('Name is required');
    setSaving(true);
    try {
      await api.post(`/api/compliance/ghi/family/${employeeId}`, form);
      setAdding(false);
      setForm({ name: '', relationship: 'Spouse', dob: '', gender: '' });
      load();
      onChanged?.();
      notify('Family member added', { title: 'Saved', variant: 'success' });
    } catch (e) { notify(e.message || 'Failed to add family member', { title: 'Error', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this member from the roster?')) return;
    setDeletingId(id);
    try {
      await api.del(`/api/compliance/ghi/family/${id}`);
      load(); onChanged?.();
      notify('Family member removed', { title: 'Saved', variant: 'success' });
    } catch (e) { notify(e.message || 'Failed to remove family member', { title: 'Error', variant: 'error' }); }
    finally { setDeletingId(null); }
  };

  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[908px] max-h-[88vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
        <div>
          <div className="text-[16px] font-extrabold text-t0 flex items-center gap-2"><Heart size={14} color={K.pu} /> GHI · {sum?.employee?.name || employeeId}</div>
          <div className="text-[14px] text-t3">{sum?.employee?.company || '—'} · {sum?.employee?.office || '—'}</div>
        </div>
        <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
      </div>
      <div className="p-5">
        {sum ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <Kpi l="Status" v={sum.status} c={sum.status === 'Active' ? K.g : sum.status === 'In Progress' ? K.a : K.t3} />
              <Kpi l="Coverage" v={fmt(sum.sumInsured)} c={K.bl} />
              <Kpi l="Premium / mo" v={fmt(sum.premiumMonthly)} c={K.pu} />
              <Kpi l="Family" v={String(sum.family.length)} c={K.pu} />
            </div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[14px] font-bold text-t0">Family Members Covered</div>
              {!adding && <Bt s="xs" icon={Plus} onClick={() => setAdding(true)}>Add Member</Bt>}
            </div>
            <div className="text-[12px] text-t3 mb-2">Enter DOB exactly as on the Aadhaar Card — insurers reject mismatched dates.</div>
            {adding && (
              <div className="mb-3 p-3 rounded-lg bg-bg2 border border-b0 grid grid-cols-1 sm:grid-cols-4 gap-2">
                <Inp value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="Full name" />
                <Sl value={form.relationship} onChange={v => setForm(f => ({ ...f, relationship: v }))}
                    options={['Self', 'Spouse', 'Child', 'Parent', 'In-Law'].map(r => ({ v: r, l: r }))} />
                <Inp type="date" value={form.dob} onChange={v => setForm(f => ({ ...f, dob: v }))} />
                <div className="flex gap-1">
                  <Bt s="xs" icon={Check} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Bt>
                  <Bt v="secondary" s="xs" onClick={() => { setAdding(false); setForm({ name: '', relationship: 'Spouse', dob: '', gender: '' }); }}>Cancel</Bt>
                </div>
              </div>
            )}
            {sum.family.length === 0 ? (
              <div className="text-[14px] text-t3 py-4 text-center">No members added yet.</div>
            ) : (
              <div className="rounded-lg border border-b0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-[14px]">
                    <thead><tr className="bg-bg2 text-t3 text-[12px] uppercase">
                      <th className="px-2 py-[6px] text-left">Name</th>
                      <th className="px-2 py-[6px] text-left">Relationship</th>
                      <th className="px-2 py-[6px] text-left">DOB</th>
                      <th className="px-2 py-[6px] text-right"></th>
                    </tr></thead>
                    <tbody>
                      {sum.family.map(m => (
                        <tr key={m.id} className="border-b border-b0">
                          <td className="px-2 py-[6px] text-t0">{m.name}</td>
                          <td className="px-2 py-[6px] text-t1">{m.relationship}</td>
                          <td className="px-2 py-[6px] text-t2">{m.dob ? fmtDMY(m.dob) : '—'}</td>
                          <td className="px-2 py-[6px] text-right"><Bt v="ghost" s="xs" icon={Trash2} onClick={() => del(m.id)} disabled={deletingId === m.id} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="mt-4 text-[12px] text-t3">
              Premium and coverage flow from the employee's Salary Structure (GHI component) — edit them from the profile Salary & Payroll tab, not here.
            </div>
          </>
        ) : <div className="text-[14px] text-t3">Loading…</div>}
      </div>
    </div>
  </div>;
};

const Kpi = ({ l, v, c }) => (
  <div className="rounded-lg border border-b0 p-2 bg-bg2">
    <div className="text-[12px] text-t3 uppercase font-bold">{l}</div>
    <div className="text-[15px] font-extrabold" style={{ color: c }}>{v}</div>
  </div>
);
